import { h as __h } from "preact";
import { Component, h } from "preact";

/** @import { enterpriseAPIKeyResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";

/** @typedef {(json: { name: string; description: string }) => Promise<boolean>} SubmitCallback */

/**
 * @param {{ enterpriseId: string }} props
 * @param {enterpriseAPIKeyResponse} apiKey
 */
export const editApiKey = async ({
  enterpriseId
}, apiKey) => {
  /** @type {SubmitCallback} */
  const onSubmit = async json => {
    await authedApiRequest("PATCH", `/v2/enterprises/${enterpriseId}/api-keys/${apiKey.id}`, {
      json
    });
    Query.invalidate([`/v2/enterprises/${enterpriseId}/api-keys`]);
    alert(`Updated API key '${json.name}'`, {
      type: "success"
    });
    return true;
  };
  return await modal({
    title: "Edit API key",
    content: (/** @type {(result?: boolean) => void} */resolve) => h(ModalChildren, {
      resolve,
      apiKey,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {enterpriseAPIKeyResponse} apiKey
 * @property {(result?: boolean) => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      name: string,
      description: string
    }), Object.fromEntries(form.entries()));
    this.props.resolve(await this.props.onSubmit(data));
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Key name",
      validity: {
        valueMissing: "Please enter a name for the key."
      },
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      defaultValue: this.props.apiKey.name,
      placeholder: this.props.apiKey.name,
      autofocus: true
    })), __h(Field, {
      "for": "description",
      label: "Key description (optional)",
      error: error?.fields.description?.message
    }, __h("input", {
      "class": input.input,
      id: "description",
      name: "description",
      defaultValue: this.props.apiKey.description,
      placeholder: this.props.apiKey.description
    })), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Update key"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}